/**
 * Corporate identity of the operating company, mirrored from the main brand site
 * (biaoperasyonel.com) so this platform's header and footer read as part of it.
 *
 * Every value is env-overridable (ADR 0015 — brand facts are deployment config, not
 * code); the defaults are the current biaoperasyonel.com values. `VITE_*` are
 * **build-time** (docs/DEPLOY.md §6), so changing one means re-build + re-deploy.
 * All five are declared in `vite-env.d.ts` — add new ones there too, or a typo
 * types as `any` and silently falls through to the default forever.
 *
 * **Setting a var to the empty string omits that item** rather than falling back:
 * a deployment that must not publish a contact detail (or a fork of this app for a
 * different operator) can suppress it without a code change. Omitting the var
 * entirely keeps the default — that is what every current deployment wants, since
 * this *is* that company's auction platform, and ADR 0015's fail-fast requirement
 * targets undecided *hosts*, not decided corporate facts.
 */
const env = import.meta.env;

/** Resolve an override: unset ⇒ default, explicit empty string ⇒ omitted. */
const value = (override: string | undefined, fallback: string): string =>
  override === undefined ? fallback : override;

/** Legal/company name in the footer copyright — distinct from the product `BRAND`. */
export const COMPANY = value(env.VITE_COMPANY_NAME, 'BİA Operasyonel');

/** Main brand site root, without a trailing slash. Empty ⇒ no corporate links. */
export const SITE_URL = value(env.VITE_BRAND_SITE_URL, 'https://biaoperasyonel.com').replace(
  /\/$/,
  '',
);

/** Absolute URL for a path on the main brand site. */
export const siteUrl = (path: string): string => `${SITE_URL}${path}`;

/** Any field may be empty — the header/footer drop the row rather than render a blank. */
export const CONTACT = {
  phone: value(env.VITE_CONTACT_PHONE, '+90 850 260 12 52'),
  email: value(env.VITE_CONTACT_EMAIL, 'info@biaoperasyonel.com'),
  address: value(
    env.VITE_CONTACT_ADDRESS,
    'Eyüp Sultan Mahallesi Vadi Sokak No: 10, 34885 Sancaktepe/İstanbul',
  ),
} as const;

/** `tel:` needs the bare international form — strip spaces and separators. */
export const telHref = `tel:${CONTACT.phone.replace(/[^\d+]/g, '')}`;

/**
 * Social profiles, in the brand site's order. `name` is a proper noun (never
 * translated) and doubles as the icon's accessible name; {@link SocialName} keys
 * the icon map in `components/SocialLinks.tsx`, so adding an entry here without an
 * icon is a compile error rather than a runtime crash.
 */
export const SOCIAL = [
  { name: 'Facebook', href: 'https://www.facebook.com/biaoperasyonelojistik/' },
  { name: 'Instagram', href: 'https://www.instagram.com/biaoperasyonelojistik/' },
  { name: 'YouTube', href: 'https://www.youtube.com/@BiaOperasyonel' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/biaoperasyonelojistik' },
] as const;

export type SocialName = (typeof SOCIAL)[number]['name'];

/** Corporate pages on the main brand site, linked from the footer. */
export const CORPORATE_LINKS = [
  { key: 'bidder.site.home', href: siteUrl('/') },
  { key: 'bidder.site.about', href: siteUrl('/hakkimizda') },
  { key: 'bidder.site.services', href: siteUrl('/hizmetler') },
  { key: 'bidder.site.contact', href: siteUrl('/iletisim') },
] as const;

/** KVKK + legal texts live on the brand site (single source for the whole group). */
export const LEGAL_URL = siteUrl('/kvkk-ve-yasal-metinler');
